import { bezSierotek } from '@insolitum/ui';
import { Lock, AlertCircle, Check, ArrowLeft, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  IKONA_OSTRZEZENIA,
  IKONA_SUKCESU,
  POLE_WEJSCIA,
  UkladWejscia,
} from '../../components/marka/UkladWejscia';
import { supabase } from '../../lib/supabase';

/**
 * Ekran ustawienia nowego hasła.
 * =============================
 *
 * Powstał, bo trasa `/reset-password` w ogóle nie istniała — wszystkie trzy
 * ścieżki resetu (ForgotPassword, panel admina, `admin-user-actions`) kierowały
 * na nią, a router odbijał je catch-allem na `/`. Konto zaproszone przez
 * `inviteUserByEmail` NIE MA hasła: pierwsza sesja z linku działała, a potem nie
 * było jak go ustawić — pętla „przypomnij hasło → link → strona główna".
 *
 * Ten sam ekran obsługuje oba wejścia, bo mechanizm jest identyczny: link
 * (recovery ALBO invite) tworzy przejściową sesję (Supabase wychwytuje token
 * z URL — `detectSessionInUrl: true`), a strona woła `updateUser({ password })`.
 */

const LIMIT_OCZEKIWANIA_NA_SESJE_MS = 12_000;
const MIN_DLUGOSC_HASLA = 8;

type Stan = 'sprawdzam' | 'gotowy' | 'brak_sesji';

function bladZUrl(): { kod: string; opis: string } | null {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);
  const kod = hash.get('error_code') || query.get('error_code');
  const error = hash.get('error') || query.get('error');
  if (!kod && !error) return null;
  return {
    kod: kod || error || '',
    opis: hash.get('error_description') || query.get('error_description') || '',
  };
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [haslo, setHaslo] = useState('');
  const [powtorz, setPowtorz] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [stan, setStan] = useState<Stan>('sprawdzam');

  useEffect(() => {
    // Wygasły/nieprawidłowy link wraca z parametrami błędu (hash w torze
    // implicit, query w PKCE) — pokazujemy to od razu, bez czekania na sesję.
    const blad = bladZUrl();
    if (blad) {
      setStan('brak_sesji');
      return;
    }

    let zrobione = false;
    const oznaczGotowy = () => {
      if (!zrobione) {
        zrobione = true;
        setStan('gotowy');
      }
    };

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) oznaczGotowy();
    });

    // Token z linku bywa wychwytywany chwilę po zamontowaniu — nasłuchujemy
    // zdarzenia zamiast zakładać, że sesja jest od razu.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) oznaczGotowy();
    });

    // Spinner bez limitu czasu to ta sama pułapka, co na akceptacji zaproszenia:
    // po 12 s bez sesji mówimy wprost, że link nie zadziałał.
    const timer = setTimeout(() => {
      if (!zrobione) setStan('brak_sesji');
    }, LIMIT_OCZEKIWANIA_NA_SESJE_MS);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (haslo.length < MIN_DLUGOSC_HASLA) {
      setError(
        t('auth.passwordTooShort', `Hasło musi mieć co najmniej ${MIN_DLUGOSC_HASLA} znaków`)
      );
      return;
    }
    if (haslo !== powtorz) {
      setError(t('auth.passwordsDoNotMatch', 'Hasła nie są identyczne'));
      return;
    }

    setIsLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: haslo });
      if (updateError) throw updateError;
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('errors.somethingWentWrong', 'Coś poszło nie tak')
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Każdy stan (sprawdzam, gotowy, brak sesji, sukces) w tym samym układzie:
  // znak i przełącznik języka zostają na miejscu, zmienia się treść pod nimi.
  if (success) {
    return (
      <UkladWejscia>
        <div className="card rounded-ids-2xl shadow-elev-4 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
            <Check className={`w-8 h-8 ${IKONA_SUKCESU}`} />
          </div>
          <h1 className="ids-tytul-ekranu">{t('auth.passwordUpdated', 'Hasło ustawione')}</h1>
          <p className="ids-tresc-ekranu mx-auto">
            {t('auth.passwordUpdatedDesc', 'Możesz się teraz zalogować nowym hasłem.')}
          </p>
          <Link to="/login" className="btn btn--primary btn--lg mt-8">
            <ArrowLeft className="w-4 h-4" />
            {t('auth.backToLogin', 'Wróć do logowania')}
          </Link>
        </div>
      </UkladWejscia>
    );
  }

  if (stan === 'brak_sesji') {
    return (
      <UkladWejscia>
        <div className="card rounded-ids-2xl shadow-elev-4 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-4">
            <Clock className={`w-8 h-8 ${IKONA_OSTRZEZENIA}`} />
          </div>
          <h1 className="ids-tytul-ekranu">{t('auth.resetLinkExpiredTitle', 'Link wygasł')}</h1>
          <p className="ids-tresc-ekranu mx-auto">
            {bezSierotek(
              t(
                'auth.resetLinkExpired',
                'Ten link do ustawienia hasła stracił ważność. Poproś o nowy — dojdzie w mailu.'
              )
            )}
          </p>
          <Link to="/forgot-password" className="btn btn--primary btn--lg mt-8">
            {t('auth.requestNewLink', 'Poproś o nowy link')}
          </Link>
        </div>
      </UkladWejscia>
    );
  }

  return (
    <UkladWejscia>
      <div className="text-center">
        <h1 className="ids-tytul-ekranu">{t('auth.setNewPassword', 'Ustaw nowe hasło')}</h1>
        <p className="ids-tresc-ekranu mx-auto">
          {t('auth.setNewPasswordDesc', 'Wpisz nowe hasło do swojego konta.')}
        </p>
      </div>

      <div className="card rounded-ids-2xl shadow-elev-4 p-8 mt-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-ids-md text-red-400 [[data-mode=light]_&]:text-red-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {bezSierotek(error)}
            </div>
          )}

          <div className="field">
            <label htmlFor="haslo" className="field__label">
              {t('auth.newPassword', 'Nowe hasło')}
            </label>
            <div className="inputwrap">
              <Lock />
              <input
                id="haslo"
                type="password"
                value={haslo}
                onChange={(e) => setHaslo(e.target.value)}
                required
                autoComplete="new-password"
                className={`input ${POLE_WEJSCIA}`}
                placeholder="••••••••"
                disabled={stan !== 'gotowy'}
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="powtorz" className="field__label">
              {t('auth.repeatPassword', 'Powtórz hasło')}
            </label>
            <div className="inputwrap">
              <Lock />
              <input
                id="powtorz"
                type="password"
                value={powtorz}
                onChange={(e) => setPowtorz(e.target.value)}
                required
                autoComplete="new-password"
                className={`input ${POLE_WEJSCIA}`}
                placeholder="••••••••"
                disabled={stan !== 'gotowy'}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || stan !== 'gotowy'}
            className="btn btn--primary btn--block"
          >
            {isLoading || stan === 'sprawdzam' ? (
              <span className="spinner" />
            ) : (
              <>
                <Lock className="w-5 h-5" />
                {t('auth.setNewPassword', 'Ustaw nowe hasło')}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-aurora-muted hover:text-aurora-text text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('auth.backToLogin', 'Wróć do logowania')}
          </Link>
        </div>
      </div>
    </UkladWejscia>
  );
}
